import { Link, NavLink, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { Reveal, Eyebrow } from "../components/Reveal";
import { CaseCard } from "../components/CaseCard";
import { CtaBand } from "../components/CtaBand";
import { Pictogram } from "../components/brand/Pictogram";
import { USE_CASES, CASES, IMG, themeBySlug } from "../data/content";

export const missionSlug = (u) => u.id.replace(/^uc-/, "");

const MissionNav = ({ current }) => (
  <nav data-testid="mission-subnav" className="sticky top-16 lg:top-20 z-30 bg-paper/95 backdrop-blur border-b border-ink/10">
    <div className="mx-auto max-w-7xl px-5 lg:px-8 flex gap-1 overflow-x-auto">
      {USE_CASES.map((u) => {
        const t = themeBySlug(u.theme);
        const active = missionSlug(u) === current;
        return (
          <NavLink key={u.id} to={`/missions/${missionSlug(u)}`} data-testid={`mission-subnav-${missionSlug(u)}`} ref={active ? (el) => { if (el) el.parentElement.scrollLeft = el.offsetLeft - 20; } : undefined}
            className={`shrink-0 px-4 py-4 text-sm font-medium border-b-2 transition-colors ${active ? "text-ink" : "text-ink/55 border-transparent hover:text-ink"}`}
            style={active ? { borderColor: t.color } : {}}>
            {u.title}
          </NavLink>
        );
      })}
    </div>
  </nav>
);

export default function MissionDetail() {
  const { slug } = useParams();
  const idx = USE_CASES.findIndex((u) => missionSlug(u) === slug);
  if (idx < 0) return <Navigate to="/missions" replace />;
  const u = USE_CASES[idx];
  const t = themeBySlug(u.theme);
  const prev = USE_CASES[(idx - 1 + USE_CASES.length) % USE_CASES.length];
  const next = USE_CASES[(idx + 1) % USE_CASES.length];
  const refs = CASES.filter((c) => c.theme === u.theme);

  return (
    <div data-testid={`mission-page-${slug}`}>
      <PageHero eyebrow={`Missions · ${t.name}`} color={t.color} title={u.title} lead={u.text} />
      <MissionNav current={slug} />
      <section className="mx-auto max-w-7xl px-5 lg:px-8 py-16 lg:py-24 grid lg:grid-cols-12 gap-10">
        <Reveal className="lg:col-span-7">
          <div className="relative aspect-[16/10] overflow-hidden bg-ink">
            <img src={IMG[u.image]} alt={u.title} className="h-full w-full object-cover" />
            <span className="absolute top-0 left-0 h-1 w-full" style={{ background: t.color }} />
          </div>
        </Reveal>
        <Reveal delay={0.1} className="lg:col-span-5 flex flex-col">
          <Pictogram name={t.icon} color={t.color} size={48} />
          <div className="mt-6 flex flex-wrap gap-2" data-testid="mission-tags">
            {u.tags.map((tag) => <span key={tag} className="text-xs px-2.5 py-1 border border-ink/15 text-ink/75">{tag}</span>)}
          </div>
          <Link to={`/services/${t.slug}`} data-testid="mission-service-link" className="btn-outline mt-8 self-start">{t.name} <ArrowRight size={16} /></Link>
        </Reveal>
      </section>
      {refs.length > 0 && (
        <section className="mx-auto max-w-7xl px-5 lg:px-8 pb-16 lg:pb-24" data-testid="mission-references">
          <Eyebrow color={t.color}>Références</Eyebrow>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {refs.map((c, i) => <Reveal key={c.id} delay={(i % 3) * 0.06}><CaseCard c={c} /></Reveal>)}
          </div>
        </section>
      )}
      <div className="mx-auto max-w-7xl px-5 lg:px-8 pb-16 flex justify-between gap-4">
        <Link to={`/missions/${missionSlug(prev)}`} data-testid="mission-prev-link" className="flex items-center gap-2 text-sm font-medium text-ink/70 hover:text-ink"><ArrowLeft size={16} /> {prev.title}</Link>
        <Link to={`/missions/${missionSlug(next)}`} data-testid="mission-next-link" className="flex items-center gap-2 text-sm font-medium text-ink/70 hover:text-ink text-right">{next.title} <ArrowRight size={16} /></Link>
      </div>
      <CtaBand color={t.color} service={t.slug} />
    </div>
  );
}
